import { useEffect, useId, useRef } from "react";

import { supabase } from "~/integrations/supabase/client";

/**
 * Subscribes to all `sales_request` changes and invokes a debounced callback.
 *
 * Column hooks that fetch once on mount (accepted / prepaid / por_facturar) use
 * this to refresh live when a sales request changes status, so cards enter and
 * leave those columns in real time instead of going stale until a page reload.
 *
 * Mirrors the realtime pattern in `useSalesRequests` (unique `useId` channel,
 * `event: "*"`, 500ms debounce, `removeChannel` cleanup). The callback is held
 * in a ref so it can change without tearing down and re-subscribing the channel.
 */
export function useSalesRequestRealtimeRefresh(onChange: () => void) {
  const instanceId = useId();
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const channel = supabase
      .channel(`sr-col-refresh-${instanceId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "sales_request" },
        () => {
          if (debounceTimer) clearTimeout(debounceTimer);
          debounceTimer = setTimeout(() => onChangeRef.current(), 500);
        }
      )
      .subscribe();

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(channel);
    };
  }, [instanceId]);
}
